/**
 * PRD.305 — the ONE ordering drawer, shared by the Laboratory module, Patient
 * Workspace Labs and Consultation Labs.
 *
 * Wherever it is opened from, it writes the same canonical order and the order
 * lands in the same Laboratory queue — the PRD's requirement that clinical
 * ordering and laboratory operations share one model, not three.
 *
 * The catalog is `appointment_services` rows flagged `is_lab_test`. Free-text
 * tests are allowed for one-offs; they simply carry no `service_id` and so are
 * not billable against a catalog price.
 *
 * Billing is OPTIONAL and explicit. PRD §4 lets an order exist before payment
 * where the clinic's own rules allow it, so this drawer never forces an
 * invoice — it offers one, reusing the existing billing subsystem. Payment
 * never gates order creation or entry into the Laboratory queue.
 *
 * When opened WITHOUT a patient context (the Laboratory module's
 * "+ New Lab Order"), it becomes a step wizard modelled directly on PRD.290's
 * New Pharmacy Sale, so the two operational modules read the same way:
 *
 *   1. Search  — existing-patient search + today's LAB REQUESTS across all
 *                patients. A "lab request" is the laboratory analog of a
 *                prescription: an order a clinician already raised that still
 *                has tests nobody has billed and settled.
 *   2. Choose  — once a patient is picked, the list is replaced by THAT
 *                patient's requests, sectioned Today / Earlier / Already
 *                billed. Exactly one eligible → auto-selected. None → continue
 *                straight to a new order.
 *   3. Order   — either bill the picked request's unbilled tests, or build a
 *                brand-new order (catalog + one-off tests, notes, billing).
 *
 * No writes happen on patient or request selection. A consultation may
 * optionally be attached to a NEW order — the open encounter is offered first —
 * but a walk-in order needs no visit at all.
 */
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  qLabTestCatalog,
  qPatientsDrawerList,
  qEncountersTodayByPatient,
  qTodaysLabRequests,
  qPatientLabRequests,
  qLabOrderTests,
  qNoOp,
  buildLabOrderTestDrafts,
  labOrderDraftTotalCents,
  resolveLabOrderAnchor,
  isLabRequestEligible,
  labRequestStatusHint,
  sectionLabRequests,
  unbilledLabTestIds,
  type LabTestCatalogRow,
  type PatientDrawerRow,
  type LabOrderEncounterCandidate,
  type LabRequestRow,
  type LabOrderTestRow,
  type TreatmentRecordDrawerWidth,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { createLabOrder, billLabTests } from '../../services/laboratory-service';
import { DRAWER_WIDTH_BY_PRESET } from '../patient/encounters/TreatmentChartingFilesDrawer';
import { LabEmptyState, LabStatusBadge, formatLabDate } from './lab-shared';

export interface LabOrderContext {
  patientId: string;
  patientName: string;
  clinicId?: string | null;
  visitId?: string | null;
  encounterId?: string | null;
  appointmentId?: string | null;
  queueId?: string | null;
  orderingProviderId?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-resolved patient context. `null` + `allowPatientSelection` = walk-in. */
  context: LabOrderContext | null;
  /** Which surface raised the order — recorded on `lab_orders.source`. */
  source: 'consultation' | 'patient_workspace' | 'laboratory';
  /** Whether the current user may add the tests to an invoice. */
  canBill?: boolean;
  /**
   * Show the patient/request wizard when no `context` is supplied. Used by the
   * Laboratory module, which has no patient in scope until one is chosen.
   */
  allowPatientSelection?: boolean;
  /**
   * Billing drawer width preset, so the wizard matches New Pharmacy Sale's
   * footprint. Ignored in the narrow in-chart mode.
   */
  drawerWidth?: TreatmentRecordDrawerWidth;
  onCreated?: (orderId: string) => void;
}

interface SelectedPatient {
  id: string;
  name: string;
  number: string | null;
}

// ─── Shared bits (mirroring New Pharmacy Sale's row/list vocabulary) ───

const fieldLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
};

const textInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 'var(--font-size-sm)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  outline: 'none',
  boxSizing: 'border-box',
};

const searchIconStyle: CSSProperties = {
  position: 'absolute',
  left: 12,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'flex',
  pointerEvents: 'none',
  color: 'var(--text-muted)',
};

const listBoxStyle: CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
};

const resultsBoxStyle: CSSProperties = {
  ...listBoxStyle,
  maxHeight: 240,
  overflowY: 'auto',
};

const mutedRowStyle: CSSProperties = {
  padding: 'var(--sp-3)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const dashedEmptyStyle: CSSProperties = {
  padding: 'var(--sp-5)',
  border: '1px dashed var(--border)',
  borderRadius: 'var(--radius-md)',
  textAlign: 'center',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
};

const rowTitleStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const rowSubStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
};

const hintStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--text-muted)',
  lineHeight: 1.5,
};

const resultRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  width: '100%',
  textAlign: 'left',
  padding: 'var(--sp-3)',
  background: 'transparent',
  border: 'none',
  borderBottom: '1px solid var(--border)',
  cursor: 'pointer',
};

/** The selected-patient bar, with a Change affordance back to the search step. */
function PatientSummaryBar({
  patient,
  onChange,
}: {
  patient: SelectedPatient;
  onChange: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
        background: 'var(--bg-surface-2)',
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={rowTitleStyle}>{patient.name}</span>
        {patient.number && <span style={rowSubStyle}>P-{patient.number}</span>}
      </span>
      <Button variant="outline" size="sm" onClick={onChange}>
        Change
      </Button>
    </div>
  );
}

/**
 * One lab-request row. Mirrors Pharmacy's prescription row: identity on the
 * left, a billing-progress hint on the right, and a single Use action.
 */
function LabRequestListRow({
  row,
  showPatient = false,
  disabled = false,
  onUse,
}: {
  row: LabRequestRow;
  showPatient?: boolean;
  disabled?: boolean;
  onUse: (row: LabRequestRow) => void;
}) {
  const tests = Number(row.billable_tests ?? 0);
  const meta = [
    row.order_number ?? 'Lab request',
    formatLabDate(row.ordered_at),
    row.provider_name,
    `${tests} test${tests === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3)',
        borderBottom: '1px solid var(--border)',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={rowTitleStyle}>
          {showPatient ? (row.patient_name ?? 'Unknown') : (row.order_number ?? 'Lab request')}
        </span>
        <span style={rowSubStyle}>
          {showPatient ? meta : meta.split(' · ').slice(1).join(' · ')}
        </span>
      </span>
      <span style={{ ...rowSubStyle, whiteSpace: 'nowrap' }}>{labRequestStatusHint(row)}</span>
      <Button variant="outline" size="sm" disabled={disabled} onClick={() => onUse(row)}>
        Use
      </Button>
    </div>
  );
}

export function CreateLabOrderDrawer({
  open,
  onOpenChange,
  context,
  source,
  canBill = false,
  allowPatientSelection = false,
  drawerWidth = 'standard',
  onCreated,
}: Props) {
  const brandTokens = useBrandTokens();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const formatMoney = useFormatMoney();

  const { data: catalog = [] } = usePowerSyncQuery<LabTestCatalogRow>(qLabTestCatalog());

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [freeText, setFreeText] = useState('');
  const [freeTextTests, setFreeTextTests] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [addToInvoice, setAddToInvoice] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Wizard state (Laboratory module walk-in) ──
  const [step, setStep] = useState<'search' | 'choose' | 'order'>('search');
  const [patientSearch, setPatientSearch] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [pickedPatient, setPickedPatient] = useState<SelectedPatient | null>(null);
  /** A clinician-raised request being billed, instead of creating a new order. */
  const [pickedRequest, setPickedRequest] = useState<LabRequestRow | null>(null);
  /**
   * Chosen consultation. `undefined` = follow the default (open encounter, if
   * any); `null` = the user explicitly chose "No consultation".
   */
  const [encounterChoice, setEncounterChoice] = useState<string | null | undefined>(undefined);

  const wizard = allowPatientSelection && !context;
  const activePatientId = context?.patientId ?? pickedPatient?.id ?? '';
  const todayIso = useMemo(() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }, []);

  const { data: patientRows = [] } = usePowerSyncQuery<PatientDrawerRow>(
    wizard && open
      ? qPatientsDrawerList({ userId, search: patientSearch, tab: 'all' })
      : qNoOp('lab-order-patients'),
  );

  // Section A — today's unsettled requests across all patients.
  const { data: todaysRequests = [] } = usePowerSyncQuery<LabRequestRow>(
    wizard && open ? qTodaysLabRequests(todayIso) : qNoOp('lab-requests-today'),
  );

  // Section B — the chosen patient's full request history (any date).
  const { data: patientRequests = [] } = usePowerSyncQuery<LabRequestRow>(
    wizard && pickedPatient ? qPatientLabRequests(pickedPatient.id) : qNoOp('lab-requests-patient'),
  );

  // Tests of the picked request — the "Add to Invoice" target.
  const { data: requestTests = [] } = usePowerSyncQuery<LabOrderTestRow>(
    pickedRequest ? qLabOrderTests(pickedRequest.lab_order_id) : qNoOp('lab-request-tests'),
  );

  const sections = useMemo(
    () => sectionLabRequests(patientRequests, todayIso),
    [patientRequests, todayIso],
  );

  const billableTestIds = useMemo(() => unbilledLabTestIds(requestTests), [requestTests]);

  // Optional consultation attach — open encounters sort first (PRD.305 §2).
  const { data: encounterRows = [] } = usePowerSyncQuery<
    LabOrderEncounterCandidate & { provider_name?: string | null }
  >(
    wizard && activePatientId && !pickedRequest
      ? qEncountersTodayByPatient(activePatientId)
      : qNoOp('lab-order-encounters'),
  );

  const anchor = useMemo(
    () => resolveLabOrderAnchor(encounterRows, encounterChoice),
    [encounterRows, encounterChoice],
  );

  // ── Transitions (no writes) ──
  const choosePatient = useCallback(
    (p: PatientDrawerRow) => {
      const eligible = todaysRequests.filter((r) => r.patient_id === p.id);
      setPickedPatient({ id: p.id, name: p.full_name ?? 'Unknown', number: p.patient_number });
      setEncounterChoice(undefined);
      if (eligible.length === 1) {
        // Exactly one waiting request → skip the extra step, as Pharmacy does.
        setPickedRequest(eligible[0]);
        setStep('order');
      } else {
        setPickedRequest(null);
        setStep('choose');
      }
    },
    [todaysRequests],
  );

  const useRequest = useCallback((r: LabRequestRow) => {
    if (!r.patient_id) return;
    setPickedPatient(
      (prev) =>
        prev ?? {
          id: r.patient_id as string,
          name: r.patient_name ?? 'Unknown',
          number: r.patient_number,
        },
    );
    setPickedRequest(r);
    setStep('order');
  }, []);

  const startNewOrder = useCallback(() => {
    setPickedRequest(null);
    setStep('order');
  }, []);

  const changePatient = useCallback(() => {
    setPickedPatient(null);
    setPickedRequest(null);
    setEncounterChoice(undefined);
    setStep('search');
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setFreeText('');
    setFreeTextTests([]);
    setNotes('');
    setSearch('');
    setError(null);
    // PRD.305 — a consultation request may be taken to an outside laboratory,
    // so billing is available but NOT pre-selected there. The clinic bills when
    // it accepts the work.
    setAddToInvoice(canBill && source !== 'consultation');
    setPatientSearch('');
    setSearchFocused(false);
    setPickedPatient(null);
    setPickedRequest(null);
    setEncounterChoice(undefined);
    setStep(allowPatientSelection && !context ? 'search' : 'order');
  }, [open, context, allowPatientSelection, canBill, source]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return catalog;
    return catalog.filter(
      (t) =>
        (t.name ?? '').toLowerCase().includes(q) ||
        (t.category_name ?? '').toLowerCase().includes(q),
    );
  }, [catalog, search]);

  /**
   * The exact rows that will be written. Built by the shared, unit-tested
   * builder rather than inline, so the drawer's preview and the persisted order
   * can never disagree — and so non-selectable services are dropped by the same
   * rule everywhere.
   */
  const drafts = useMemo(
    () =>
      buildLabOrderTestDrafts({
        catalog,
        selectedIds: [...selected],
        freeTextNames: freeTextTests,
      }),
    [catalog, selected, freeTextTests],
  );

  const totalCents = useMemo(() => labOrderDraftTotalCents(drafts), [drafts]);
  const testCount = drafts.length;

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const addFreeText = useCallback(() => {
    const name = freeText.trim();
    if (!name) return;
    setFreeTextTests((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setFreeText('');
  }, [freeText]);

  /**
   * The patient + anchor this order will be written against. A supplied
   * `context` (Patient Workspace / Consultation) wins; otherwise it comes from
   * the walk-in picker, where BOTH anchor ids may legitimately be null.
   */
  const effectiveContext = useMemo<LabOrderContext | null>(() => {
    if (context) return context;
    if (!pickedPatient) return null;
    return {
      patientId: pickedPatient.id,
      patientName: pickedPatient.name,
      encounterId: anchor.encounterId,
      visitId: anchor.visitId,
    };
  }, [context, pickedPatient, anchor]);

  /**
   * Bill an existing clinician-raised request. This creates NO order — it only
   * adds the still-unbilled tests to the patient's invoice through the same
   * `billLabTests` path the create flow uses, so a partially billed request can
   * be re-opened without double-charging.
   */
  const handleBillRequest = useCallback(async () => {
    if (!db || !pickedRequest || busy || billableTestIds.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await billLabTests({
        db,
        orgId,
        patientId: pickedRequest.patient_id ?? '',
        patientName: pickedRequest.patient_name ?? 'Patient',
        testIds: billableTestIds,
        encounterId: pickedRequest.encounter_id ?? null,
        visitId: pickedRequest.visit_id ?? null,
        date: new Date().toISOString().slice(0, 10),
      });
      onCreated?.(pickedRequest.lab_order_id);
      onOpenChange(false);
    } catch (err) {
      console.error('[CreateLabOrder] billing an existing request failed:', err);
      setError(err instanceof Error ? err.message : 'Could not add these tests to the invoice.');
    } finally {
      setBusy(false);
    }
  }, [db, pickedRequest, busy, billableTestIds, orgId, onCreated, onOpenChange]);

  const handleCreate = useCallback(async () => {
    if (!db || !effectiveContext || busy || testCount === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { orderId, testIds } = await createLabOrder({
        db,
        orgId,
        clinicId: effectiveContext.clinicId ?? null,
        patientId: effectiveContext.patientId,
        visitId: effectiveContext.visitId ?? null,
        encounterId: effectiveContext.encounterId ?? null,
        appointmentId: effectiveContext.appointmentId ?? null,
        queueId: effectiveContext.queueId ?? null,
        orderingProviderId: effectiveContext.orderingProviderId ?? null,
        actor: { uid: user?.uid ?? '', name: user?.displayName ?? user?.email ?? null },
        source,
        tests: drafts,
        notes: notes.trim() || null,
      });

      // Billing is a separate, optional step — a failure here must not undo an
      // order that is already live in the Laboratory queue, so it is reported
      // rather than thrown past the created order.
      if (canBill && addToInvoice) {
        try {
          await billLabTests({
            db,
            orgId,
            patientId: effectiveContext.patientId,
            patientName: effectiveContext.patientName,
            testIds,
            encounterId: effectiveContext.encounterId ?? null,
            visitId: effectiveContext.visitId ?? null,
            date: new Date().toISOString().slice(0, 10),
          });
        } catch (billErr) {
          console.error('[CreateLabOrder] order created but billing failed:', billErr);
        }
      }

      onCreated?.(orderId);
      onOpenChange(false);
    } catch (err) {
      console.error('[CreateLabOrder] failed:', err);
      setError(err instanceof Error ? err.message : 'Could not create the laboratory order.');
    } finally {
      setBusy(false);
    }
  }, [
    db,
    effectiveContext,
    busy,
    testCount,
    drafts,
    orgId,
    user,
    source,
    notes,
    canBill,
    addToInvoice,
    onCreated,
    onOpenChange,
  ]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title="New Lab Order"
      description={
        wizard
          ? step === 'order'
            ? pickedRequest
              ? `Billing — ${pickedPatient?.name ?? ''}`
              : `New order — ${pickedPatient?.name ?? ''}`
            : step === 'choose'
              ? 'Choose a lab request, or continue with a new order.'
              : 'Search a patient or pick a lab request to begin.'
          : effectiveContext?.patientName
      }
      width={wizard ? DRAWER_WIDTH_BY_PRESET[drawerWidth] : 520}
      portalStyle={brandTokens}
      footer={
        // Steps 1–2 advance by selection, exactly like New Pharmacy Sale — a
        // footer there would imply a commit that hasn't been configured yet.
        wizard && step !== 'order' ? undefined : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--sp-3)',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              {pickedRequest
                ? billableTestIds.length === 0
                  ? 'Every test is already invoiced'
                  : `${billableTestIds.length} test${
                      billableTestIds.length === 1 ? '' : 's'
                    } to bill`
                : testCount === 0
                  ? 'No tests selected'
                  : `${testCount} test${testCount === 1 ? '' : 's'}${
                      totalCents > 0 ? ` · ${formatMoney(totalCents / 100)}` : ''
                    }`}
            </span>
            <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
              <Button variant="outline" size="md" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {pickedRequest ? (
                <Button
                  variant="gradient"
                  size="md"
                  onClick={handleBillRequest}
                  disabled={busy || billableTestIds.length === 0}
                >
                  {busy ? 'Adding…' : 'Add to Invoice'}
                </Button>
              ) : (
                <Button
                  variant="gradient"
                  size="md"
                  onClick={handleCreate}
                  disabled={busy || testCount === 0 || !effectiveContext}
                >
                  {busy ? 'Creating…' : 'Create Order'}
                </Button>
              )}
            </div>
          </div>
        )
      }
    >
      {/* `Drawer` intentionally leaves the body unpadded — each consumer sets
          its own inset. Match the header/footer's horizontal `--sp-6` so the
          content lines up with the title and buttons. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-4)',
          padding: 'var(--sp-5) var(--sp-6)',
        }}
      >
        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-3)',
              borderRadius: 'var(--radius-md)',
              background: '#fee2e2',
              color: '#991b1b',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}

        {/* ═══ Step 1 — Search: patient + today's lab requests ═══
            Mirrors New Pharmacy Sale (PRD.290): existing patients only, results
            appear on focus/typing, and nothing is written by selecting. */}
        {wizard && step === 'search' && (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={fieldLabelStyle}>Patient</label>
              <div style={{ position: 'relative' }}>
                <div style={searchIconStyle}>
                  <NavIcon name="search" />
                </div>
                <input
                  type="text"
                  placeholder="Search patient name, Patient No., ID No..."
                  value={patientSearch}
                  onChange={(e) => setPatientSearch(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                  style={{ ...textInputStyle, paddingLeft: 40 }}
                />
              </div>

              {(searchFocused || patientSearch.trim().length > 0) && (
                <div style={resultsBoxStyle}>
                  {patientRows.length === 0 ? (
                    <div style={mutedRowStyle}>No patients found</div>
                  ) : (
                    patientRows.slice(0, 25).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => choosePatient(p)}
                        style={resultRowStyle}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={rowTitleStyle}>{p.full_name ?? 'Unknown'}</div>
                          {(p.phone || p.email) && (
                            <div style={rowSubStyle}>
                              {[p.phone, p.email].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        {p.patient_number && <span style={rowSubStyle}>P-{p.patient_number}</span>}
                      </button>
                    ))
                  )}
                </div>
              )}

              <span style={hintStyle}>
                Existing patients only. Selecting a patient does not create an invoice.
              </span>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={fieldLabelStyle}>Today&rsquo;s Lab Requests</label>
              {todaysRequests.length === 0 ? (
                <div style={dashedEmptyStyle}>No lab requests waiting to be billed today.</div>
              ) : (
                <div style={listBoxStyle}>
                  {todaysRequests.map((r) => (
                    <LabRequestListRow
                      key={r.lab_order_id}
                      row={r}
                      showPatient
                      onUse={useRequest}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* ═══ Step 2 — Choose: this patient's requests, or a new order ═══ */}
        {wizard && step === 'choose' && pickedPatient && (
          <>
            <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />

            {patientRequests.length === 0 ? (
              <div style={dashedEmptyStyle}>
                No laboratory requests for this patient yet. Continue below to raise a new order.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
                {(
                  [
                    ['Today', sections.today],
                    ['Earlier', sections.earlier],
                    ['Already billed', sections.settled],
                  ] as const
                ).map(([label, rows]) =>
                  rows.length === 0 ? null : (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={fieldLabelStyle}>{label}</label>
                      <div style={listBoxStyle}>
                        {rows.map((r) => (
                          <LabRequestListRow
                            key={r.lab_order_id}
                            row={r}
                            onUse={useRequest}
                            disabled={!isLabRequestEligible(r)}
                          />
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}

            <div>
              <Button variant="outline" size="md" onClick={startNewOrder}>
                Continue with a new order
              </Button>
            </div>
          </>
        )}

        {/* ═══ Step 3a — Bill an existing request ═══
            No order is created here: the request already exists, so this only
            adds its still-unbilled tests to the invoice. */}
        {step === 'order' && pickedRequest && (
          <>
            {pickedPatient && (
              <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--sp-3)',
                padding: 'var(--sp-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface-2)',
              }}
            >
              <span style={{ minWidth: 0 }}>
                <span style={rowTitleStyle}>{pickedRequest.order_number ?? 'Lab request'}</span>
                <span style={rowSubStyle}>
                  {formatLabDate(pickedRequest.ordered_at)}
                  {pickedRequest.provider_name ? ` · ${pickedRequest.provider_name}` : ''}
                </span>
              </span>
              <Button variant="outline" size="sm" onClick={() => setStep('choose')}>
                Change
              </Button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={fieldLabelStyle}>Tests on this request</label>
              <div style={listBoxStyle}>
                {requestTests.map((t) => {
                  const billed = t.invoice_payment_status != null;
                  return (
                    <div
                      key={t.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-2)',
                        padding: 'var(--sp-3)',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={rowTitleStyle}>{t.test_name}</span>
                        <span style={rowSubStyle}>
                          {billed ? 'Already on an invoice' : 'Not yet billed'}
                        </span>
                      </span>
                      <LabStatusBadge status={t.status} />
                      {t.price_cents_snapshot != null && (
                        <span style={rowSubStyle}>{formatMoney(t.price_cents_snapshot / 100)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <span style={hintStyle}>
                Only tests that aren&rsquo;t on an invoice yet will be added, so re-opening a partly
                billed request never charges twice.
              </span>
            </div>
          </>
        )}

        {/* ═══ Step 3b — Build a new order ═══ */}
        {step === 'order' && !pickedRequest && (
          <>
            {wizard && pickedPatient && (
              <PatientSummaryBar patient={pickedPatient} onChange={changePatient} />
            )}

            {/* Optional consultation. A walk-in order needs none — the anchor
                columns are nullable precisely so no placeholder visit is
                invented. */}
            {wizard && encounterRows.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={fieldLabelStyle}>
                  Attach to consultation{' '}
                  <span style={{ color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <select
                  value={anchor.encounterId ?? ''}
                  onChange={(e) => setEncounterChoice(e.target.value || null)}
                  style={{ ...textInputStyle, cursor: 'pointer' }}
                >
                  <option value="">No consultation (walk-in)</option>
                  {encounterRows.map((e) => (
                    <option key={e.id} value={e.id}>
                      {formatLabDate(e.started_at)}
                      {e.status === 'open' ? ' · Open' : ''}
                      {e.provider_name ? ` · ${e.provider_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Test selection, one-off entry, notes and billing — the new-order
            form. Hidden while billing an existing request, which has its own
            already-decided test list. */}
        {step === 'order' && !pickedRequest && (
          <>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search laboratory tests…"
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 'var(--font-size-sm)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />

            {catalog.length === 0 ? (
              <LabEmptyState
                title="No laboratory tests in the catalog"
                description="Laboratory tests are services marked as a lab test. Add them under Appointments → Manage Services, then order them here. You can still add a one-off test below."
              />
            ) : (
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
              >
                {filtered.map((t, i) => {
                  const checked = selected.has(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => toggle(t.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        width: '100%',
                        textAlign: 'left',
                        padding: 'var(--sp-3)',
                        background: checked ? 'var(--bg-surface-2)' : 'transparent',
                        border: 'none',
                        borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 'var(--radius-xs)',
                          border: `2px solid ${checked ? 'var(--color-primary)' : 'var(--border)'}`,
                          background: checked ? 'var(--color-primary)' : 'transparent',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--text)',
                          }}
                        >
                          {t.name}
                        </span>
                        {t.category_name && (
                          <span
                            style={{
                              display: 'block',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-muted)',
                            }}
                          >
                            {t.category_name}
                          </span>
                        )}
                      </span>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                        {t.price_cents != null ? formatMoney(t.price_cents / 100) : '—'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* One-off test */}
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                Add a one-off test
              </label>
              <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                <input
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addFreeText();
                    }
                  }}
                  placeholder="Test name"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border)',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <Button
                  variant="outline"
                  size="md"
                  onClick={addFreeText}
                  disabled={!freeText.trim()}
                >
                  Add
                </Button>
              </div>
              {freeTextTests.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--sp-2)',
                    marginTop: 'var(--sp-2)',
                  }}
                >
                  {freeTextTests.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setFreeTextTests((prev) => prev.filter((n) => n !== name))}
                      title="Remove"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 'var(--sp-1)',
                        padding: '2px var(--sp-2)',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text)',
                        cursor: 'pointer',
                      }}
                    >
                      {name} ×
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Clinical notes for the laboratory (optional)"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 'var(--font-size-sm)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            {canBill && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-2)',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={addToInvoice}
                  onChange={(e) => setAddToInvoice(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  Add these tests to the patient&rsquo;s invoice
                  <span
                    style={{
                      display: 'block',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {source === 'consultation'
                      ? 'Leave this off if the patient may have the tests done elsewhere — the clinic can bill when it accepts the request.'
                      : 'Uses the existing billing flow. Ordering without billing is allowed — the laboratory will still see the order.'}
                  </span>
                </span>
              </label>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}

export default CreateLabOrderDrawer;
